import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Filter } from 'bad-words';
import { blackList } from '@vnphu/vn-badwords';
import { BsChatDots } from 'react-icons/bs';
import { useChat } from '../ChatProvider';
import chatApi from 'api/chatApi';

const MAX_LENGTH = 1000;
const RATE_LIMIT_MS = 2000;

// Extend bad-words Filter to support Vietnamese Unicode (\\b doesn't work with diacritics)
class ViFilter extends Filter {
  isProfane(string) {
    // English words: use parent (works fine with \\b)
    if (super.isProfane(string)) return true;
    // Vietnamese words: use whitespace/boundary-aware matching
    const normalized = string.normalize().toLowerCase();
    return blackList.some((word) => {
      const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`, 'i').test(normalized);
    });
  }
}

const profanityFilter = new ViFilter();

function containsBadWords(text) {
  return profanityFilter.isProfane(text);
}

const ChatWindow = ({ roomId, title, height = '100%', placeholder = 'Nhập tin nhắn...', type, disabled = false, disabledPlaceholder }) => {
  const { rcAuth, addMessageListener, setActiveRoom, clientRef } = useChat();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [warning, setWarning] = useState('');
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const offsetRef = useRef(0);
  const typingTimerRef = useRef(null);
  const lastSendTimeRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  // Mark this room as active
  useEffect(() => {
    if (roomId) {
      setActiveRoom(roomId);
      return () => setActiveRoom(null);
    }
  }, [roomId, setActiveRoom]);

  // Load initial messages
  useEffect(() => {
    if (!roomId || !rcAuth?.authToken) return;

    const loadMessages = async () => {
      setLoading(true);
      try {
        const res = await chatApi.getMessages(
          roomId,
          rcAuth.authToken,
          rcAuth.userId,
          50,
          0,
          type
        );
        const raw = res?.data?.messages || [];
        const msgs = raw.filter((m) => !m.t);
        setMessages(msgs.reverse());
        offsetRef.current = raw.length; // offset theo tổng tin từ server (bao gồm system messages)
        setHasMore(raw.length >= 50);
      } catch (err) {
        console.error('Failed to load messages:', err);
      } finally {
        setLoading(false);
      }
    };

    setMessages([]);
    offsetRef.current = 0;
    setHasMore(true);
    loadMessages();
  }, [roomId, rcAuth?.authToken, rcAuth?.userId]);

  // Scroll to bottom only on new messages (not when loading older)
  useEffect(() => {
    if (!isLoadingMoreRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    isLoadingMoreRef.current = false;
  }, [messages]);

  // Listen for real-time messages
  useEffect(() => {
    if (!roomId) return;
    const unsub = addMessageListener((rid, msg) => {
      if (rid === roomId && !msg.t) {
        setMessages((prev) => {
          if (prev.some((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
      }
    });
    return unsub;
  }, [roomId, addMessageListener]);

  // Load older messages
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !roomId || !rcAuth?.authToken) return;
    setLoading(true);
    isLoadingMoreRef.current = true;
    try {
      const res = await chatApi.getMessages(
        roomId,
        rcAuth.authToken,
        rcAuth.userId,
        50,
        offsetRef.current,
        type
      );
      const raw = res?.data?.messages || [];
      const older = raw.filter((m) => !m.t);
      if (raw.length < 50) setHasMore(false);
      setMessages((prev) => [...older.reverse(), ...prev]);
      offsetRef.current += raw.length;
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, roomId, rcAuth?.authToken, rcAuth?.userId]);

  // Handle scroll for loading older messages
  const handleScroll = () => {
    if (containerRef.current?.scrollTop === 0 && hasMore && !loading) {
      loadMore();
    }
  };

  // Send message
  const handleSend = async () => {
    if (disabled) return;
    const trimmed = text.trim();
    if (!trimmed || !roomId || !rcAuth?.authToken) return;

    // Rate limit
    const now = Date.now();
    if (now - lastSendTimeRef.current < RATE_LIMIT_MS) {
      setWarning('Bạn gửi tin nhắn quá nhanh, vui lòng chờ.');
      return;
    }

    // Max length
    if (trimmed.length > MAX_LENGTH) {
      setWarning(`Tin nhắn tối đa ${MAX_LENGTH} ký tự.`);
      return;
    }

    // Bad words
    if (containsBadWords(trimmed)) {
      setWarning('Tin nhắn chứa từ ngữ không phù hợp. Vui lòng chỉnh sửa.');
      return;
    }

    setWarning('');
    setText('');
    lastSendTimeRef.current = now;

    // Optimistic: hiện tin nhắn ngay với status pending
    const tempId = `pending-${Date.now()}`;
    const pendingMsg = {
      _id: tempId,
      msg: trimmed,
      ts: new Date().toISOString(),
      u: { _id: rcAuth.userId, username: rcAuth.username },
      _pending: true,
    };
    setMessages((prev) => [...prev, pendingMsg]);

    try {
      await chatApi.sendMessage(roomId, trimmed, rcAuth.authToken, rcAuth.userId);
      clientRef.current?.sendTyping(roomId, rcAuth.username, false);
      // Xoá pending message (WebSocket sẽ trả về message thật)
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
    } catch (err) {
      console.error('Failed to send message:', err);
      // Đánh dấu lỗi trên message
      setMessages((prev) =>
        prev.map((m) => (m._id === tempId ? { ...m, _pending: false, _failed: true } : m))
      );
    }
  };

  // Retry failed message
  const handleRetry = async (failedMsg) => {
    const tempId = failedMsg._id;
    setMessages((prev) =>
      prev.map((m) => (m._id === tempId ? { ...m, _pending: true, _failed: false } : m))
    );
    try {
      await chatApi.sendMessage(roomId, failedMsg.msg, rcAuth.authToken, rcAuth.userId);
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
    } catch {
      setMessages((prev) =>
        prev.map((m) => (m._id === tempId ? { ...m, _pending: false, _failed: true } : m))
      );
    }
  };

  // Remove failed message
  const handleRemoveFailed = (msgId) => {
    setMessages((prev) => prev.filter((m) => m._id !== msgId));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Typing indicator
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.length > MAX_LENGTH) return;
    setText(val);
    if (warning) setWarning('');
    if (roomId && clientRef.current && rcAuth?.username) {
      clientRef.current.sendTyping(roomId, rcAuth.username, true);
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        clientRef.current?.sendTyping(roomId, rcAuth.username, false);
      }, 2000);
    }
  };

  // Delete message
  const handleDelete = async (msgId) => {
    if (!window.confirm('Xoá tin nhắn này?')) return;
    try {
      await chatApi.deleteMessage(roomId, msgId, rcAuth.authToken, rcAuth.userId);
      setMessages((prev) => prev.filter((m) => m._id !== msgId));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts?.$date || ts);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })} ${time}`;
  };

  if (!roomId) {
    return (
      <EmptyState>
        <p>Đang kết nối...</p>
      </EmptyState>
    );
  }

  return (
    <Wrapper style={{ height }}>
      {title && <Header>{title}</Header>}
      <MessageList ref={containerRef} onScroll={handleScroll}>
        {loading && messages.length === 0 && (
          <LoadingText>Đang tải tin nhắn...</LoadingText>
        )}
        {hasMore && messages.length > 0 && (
          <LoadMoreBtn onClick={loadMore} disabled={loading}>
            {loading ? 'Đang tải...' : 'Tải thêm'}
          </LoadMoreBtn>
        )}
        {!loading && messages.length === 0 && (
          <WelcomeState>
            <WelcomeIcon><BsChatDots /></WelcomeIcon>
            <WelcomeTitle>Chào mừng bạn đến với lớp học!</WelcomeTitle>
            <WelcomeText>Hãy gửi tin nhắn để bắt đầu cuộc trò chuyện.</WelcomeText>
          </WelcomeState>
        )}
        {messages.map((msg) => {
          const isOwn = msg.u?._id === rcAuth?.userId;
          return (
            <MessageRow key={msg._id} $own={isOwn}>
              <MessageBubble $own={isOwn} $failed={msg._failed}>
                {!isOwn && <SenderName>{msg.u?.name || msg.u?.username}</SenderName>}
                <MessageText>{msg.msg}</MessageText>
                <MessageMeta>
                  {msg._pending ? (
                    <StatusText>Đang gửi...</StatusText>
                  ) : msg._failed ? (
                    <FailedMeta>
                      <StatusText $error>Gửi lỗi</StatusText>
                      <RetryBtn onClick={() => handleRetry(msg)}>Thử lại</RetryBtn>
                      <DeleteBtn onClick={() => handleRemoveFailed(msg._id)}>Xoá</DeleteBtn>
                    </FailedMeta>
                  ) : (
                    <>
                      <span>{formatTime(msg.ts)}</span>
                      {isOwn && (
                        <DeleteBtn onClick={() => handleDelete(msg._id)}>Xoá</DeleteBtn>
                      )}
                    </>
                  )}
                </MessageMeta>
              </MessageBubble>
            </MessageRow>
          );
        })}
        <div ref={messagesEndRef} />
      </MessageList>
      {warning && <WarningBar>{warning}</WarningBar>}
      <InputArea>
        <InputWrapper>
          <TextInput
            value={text}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? (disabledPlaceholder || placeholder) : placeholder}
            rows={1}
            disabled={disabled}
          />
          {text.length > MAX_LENGTH * 0.8 && (
            <CharCount $over={text.length >= MAX_LENGTH}>
              {text.length}/{MAX_LENGTH}
            </CharCount>
          )}
        </InputWrapper>
        <SendBtn onClick={handleSend} disabled={disabled || !text.trim()}>
          Gửi
        </SendBtn>
      </InputArea>
    </Wrapper>
  );
};

export default ChatWindow;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 12px 16px;
  font-weight: 600;
  font-size: 15px;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

const LoadingText = styled.div`
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 16px;
`;

const LoadMoreBtn = styled.button`
  align-self: center;
  background: none;
  border: none;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 12px;
  margin-bottom: 8px;
  &:hover { text-decoration: underline; }
  &:disabled { color: #94a3b8; cursor: default; text-decoration: none; }
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$own ? 'flex-end' : 'flex-start')};
`;

const MessageBubble = styled.div`
  max-width: 75%;
  padding: 8px 12px;
  border-radius: ${(p) => (p.$own ? '12px 12px 2px 12px' : '12px 12px 12px 2px')};
  background: ${(p) => (p.$failed ? '#fef2f2' : p.$own ? '#2563eb' : '#f1f5f9')};
  color: ${(p) => (p.$failed ? '#991b1b' : p.$own ? '#fff' : '#1e293b')};
  border: ${(p) => (p.$failed ? '1px solid #fecaca' : 'none')};
  word-break: break-word;
`;

const SenderName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 2px;
`;

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 10px;
  opacity: 0.7;
  justify-content: flex-end;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  font-size: 10px;
  cursor: pointer;
  color: inherit;
  opacity: 0.8;
  padding: 0;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const StatusText = styled.span`
  font-size: 10px;
  color: ${(p) => (p.$error ? '#dc2626' : 'inherit')};
  font-weight: ${(p) => (p.$error ? 600 : 400)};
`;

const FailedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RetryBtn = styled.button`
  background: none;
  border: none;
  font-size: 10px;
  cursor: pointer;
  color: #2563eb;
  padding: 0;
  font-weight: 600;
  &:hover { text-decoration: underline; }
`;

const WarningBar = styled.div`
  padding: 6px 12px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
  text-align: center;
  border-top: 1px solid #fecaca;
`;

const InputArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const TextInput = styled.textarea`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 14px;
  resize: none;
  outline: none;
  font-family: inherit;
  max-height: 80px;
  box-sizing: border-box;
  &:focus { border-color: #93c5fd; }
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const CharCount = styled.span`
  position: absolute;
  right: 12px;
  bottom: 2px;
  font-size: 10px;
  color: ${(p) => (p.$over ? '#dc2626' : '#94a3b8')};
  pointer-events: none;
`;

const SendBtn = styled.button`
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: #1d4ed8; }
  &:disabled { background: #94a3b8; cursor: default; }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;
  font-size: 14px;
`;

const WelcomeState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px 16px;
  text-align: center;
`;

const WelcomeIcon = styled.div`
  font-size: 40px;
  color: #2563eb;
  margin-bottom: 12px;
`;

const WelcomeTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
`;

const WelcomeText = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;
